import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUsers, useClients, userMap, clientMap } from "@/lib/hooks";
import { PageHeader } from "@/components/PageHeader";
import { TaskModal } from "@/components/TaskModal";
import { DayCheckInCard } from "@/components/DayCheckInCard";
import { CompletedTasksPanel } from "@/components/CompletedTasksPanel";
import { UserAvatar } from "@/components/UserAvatar";
import { PRIORITY, TASK_STATUS, TASK_STATUS_ORDER, EVENT_TYPES } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StickyNotes } from "@/components/StickyNotes";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, LayoutGrid, List, CalendarDays, MoveRight, StickyNote, Sun, Clock, CheckCircle2, Trash2, History } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, isToday, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

function PriorityDot({ p }) {
  return <span className="h-2 w-2 rounded-full inline-block" style={{ backgroundColor: PRIORITY[p]?.color }} />;
}

function TaskCard({ task, users, clients, onClick, onStatus, onDelete, draggable, dragging, onDragStart, onDragEnd }) {
  const cm = clientMap(clients);
  return (
    <div
      className={`rounded-lg border border-border bg-card p-3 hover:border-primary/40 transition-colors group ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${dragging ? "opacity-40" : ""}`}
      onClick={() => onClick(task)}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <PriorityDot p={task.priority} />
          <span className="font-poppins font-medium text-sm truncate">{task.title}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onStatus && (
            // Em ecrãs de toque não há arrastar: o estado muda por aqui.
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="md:hidden text-muted-foreground hover:text-foreground"
                  aria-label="Mudar estado"
                  data-testid={`task-status-${task.id}`}
                >
                  <MoveRight className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {TASK_STATUS_ORDER.map((st) => (
                  <DropdownMenuItem
                    key={st}
                    disabled={st === task.status}
                    onClick={(e) => { e.stopPropagation(); onStatus(task, st); }}
                  >
                    <span className="h-2 w-2 rounded-full mr-2" style={{ backgroundColor: TASK_STATUS[st].color }} />
                    {TASK_STATUS[st].label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {task.description && <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 font-roboto">{task.description}</p>}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {task.clientId && cm[task.clientId] && (
            <Badge variant="secondary" className="text-[10px] rounded-full">{cm[task.clientId].name}</Badge>
          )}
          {task.dueDate && (
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> {format(parseISO(task.dueDate), "d MMM", { locale: pt })}
            </span>
          )}
        </div>
        <div className="flex -space-x-1.5">
          {(task.assigneeIds || []).slice(0, 3).map((id) => users[id] && <UserAvatar key={id} user={users[id]} size="h-5 w-5 ring-1 ring-card" />)}
        </div>
      </div>
    </div>
  );
}

export default function Workspace() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: allUsers = [] } = useUsers();
  const { data: clients = [] } = useClients();
  const um = useMemo(() => userMap(allUsers), [allUsers]);

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks", "mine"],
    queryFn: async () => (await api.get("/tasks", { params: { mine: true } })).data,
  });

  // A gestão pode alternar o quadro para as tarefas de toda a equipa. "O Meu
  // Dia" continua sempre com as tarefas da própria pessoa.
  const isManager = ["ADMIN", "SUB_ADMIN"].includes(user.role);
  const [scope, setScope] = useState("mine");
  const [personId, setPersonId] = useState("all");
  const teamView = isManager && scope === "team";
  const { data: teamTasks = [] } = useQuery({
    queryKey: ["tasks", "team"],
    queryFn: async () => (await api.get("/tasks")).data,
    enabled: teamView,
  });
  const teamMembers = useMemo(
    () => allUsers.filter((u) => u.status !== "INATIVO").sort((a, b) => a.name.localeCompare(b.name)),
    [allUsers],
  );
  const scopeTasks = teamView
    ? teamTasks.filter((t) => personId === "all" || (t.assigneeIds || []).includes(personId))
    : tasks;
  // As concluídas não se acumulam no quadro: só as de hoje. As dos outros dias
  // ficam no separador "Concluídas".
  const boardTasks = scopeTasks.filter(
    (t) => t.status !== "CONCLUIDO" || (t.completedAt && isToday(parseISO(t.completedAt))),
  );

  const { data: events = [] } = useQuery({
    queryKey: ["events", "mine"],
    queryFn: async () => (await api.get("/events")).data,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragTask, setDragTask] = useState(null);
  const [dropColumn, setDropColumn] = useState(null);
  const [notepadOpen, setNotepadOpen] = useState(false);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setModalOpen(true); };

  const tasksKey = teamView ? ["tasks", "team"] : ["tasks", "mine"];

  const setStatus = async (task, status) => {
    if (!task || task.status === status) return;
    const previous = qc.getQueryData(tasksKey);
    // O cartão salta logo de coluna; se a gravação falhar, volta para trás.
    qc.setQueryData(tasksKey, (old = []) =>
      old.map((item) => (item.id === task.id
        ? { ...item, status, completedAt: status === "CONCLUIDO" ? new Date().toISOString() : "" }
        : item)),
    );
    try {
      await api.patch(`/tasks/${task.id}/status`, { status });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    } catch (e) {
      qc.setQueryData(tasksKey, previous);
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const deleteTask = async (id) => {
    try { await api.delete(`/tasks/${id}`); qc.invalidateQueries({ queryKey: ["tasks"] }); toast.success("Tarefa eliminada"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const myEventsToday = events.filter((e) => e.date && isToday(parseISO(e.date)) && (e.assigneeIds || []).includes(user.id));
  const myTasksToday = tasks.filter((t) => t.dueDate && isToday(parseISO(t.dueDate)) && t.status !== "CONCLUIDO");

  return (
    <div>
      <PageHeader
        title="As Minhas Tarefas"
        subtitle="Organiza o teu trabalho, acompanha prazos e mantém o foco nas prioridades."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setNotepadOpen((value) => !value)} className="rounded-lg" data-testid="notepad-btn">
              <StickyNote className="h-4 w-4 mr-1.5" /> Notas
            </Button>
            <Button onClick={openNew} className="bg-primary text-black hover:bg-primary/90 rounded-lg" data-testid="new-task-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nova Tarefa
            </Button>
          </div>
        }
      />

      <DayCheckInCard />

      {/* O Meu Dia */}
      <Card className="p-5 mb-6 border-border bg-gradient-to-br from-primary/10 to-transparent">
        <div className="flex items-center gap-2 mb-4">
          <Sun className="h-5 w-5 text-primary" />
          <h3 className="font-poppins font-semibold">O Meu Dia · Foco Diário</h3>
          <span className="text-xs text-muted-foreground">{format(new Date(), "EEEE, d 'de' MMMM", { locale: pt })}</span>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-poppins mb-2">Tarefas para hoje</p>
            {myTasksToday.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem tarefas com prazo hoje. 🎉</p>
            ) : (
              <div className="space-y-2">
                {myTasksToday.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 text-sm">
                    <button onClick={() => setStatus(t, "CONCLUIDO")} className="text-muted-foreground hover:text-primary">
                      <CheckCircle2 className="h-4 w-4" />
                    </button>
                    <PriorityDot p={t.priority} />
                    <span className="font-roboto">{t.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-poppins mb-2">Reuniões & eventos</p>
            {myEventsToday.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem eventos agendados hoje.</p>
            ) : (
              <div className="space-y-2">
                {myEventsToday.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: EVENT_TYPES[e.type]?.color }} />
                    <span className="font-roboto">{e.title}</span>
                    {!e.allDay && <span className="text-xs text-muted-foreground">{e.startTime} às {e.endTime}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      <div>
        <div>
          <Tabs defaultValue="kanban">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="kanban" data-testid="view-kanban"><LayoutGrid className="h-4 w-4 mr-1.5" /> Quadro Kanban</TabsTrigger>
                <TabsTrigger value="lista" data-testid="view-list"><List className="h-4 w-4 mr-1.5" /> Lista</TabsTrigger>
                <TabsTrigger value="calendario" data-testid="view-calendar"><CalendarDays className="h-4 w-4 mr-1.5" /> Calendário</TabsTrigger>
                <TabsTrigger value="concluidas" data-testid="view-completed"><History className="h-4 w-4 mr-1.5" /> Concluídas</TabsTrigger>
              </TabsList>
              {isManager && (
                <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                  <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="task-scope">
                    {[["mine", "As minhas"], ["team", "Equipa"]].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setScope(value)}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${scope === value ? "bg-primary text-black" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`task-scope-${value}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {teamView && (
                    <Select value={personId} onValueChange={setPersonId}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid="task-person"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toda a equipa</SelectItem>
                        {teamMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>

            <TabsContent value="kanban">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {TASK_STATUS_ORDER.map((st) => {
                  const isTarget = dragTask && dropColumn === st && dragTask.status !== st;
                  return (
                    <div
                      key={st}
                      className={`rounded-lg p-2.5 transition-colors ${isTarget ? "bg-primary/10 ring-2 ring-inset ring-primary/40" : "bg-muted/40"}`}
                      onDragOver={(e) => {
                        if (!dragTask) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dropColumn !== st) setDropColumn(st);
                      }}
                      onDragLeave={() => setDropColumn((current) => (current === st ? null : current))}
                      onDrop={(e) => {
                        e.preventDefault();
                        const task = dragTask;
                        setDragTask(null);
                        setDropColumn(null);
                        if (task) setStatus(task, st);
                      }}
                      data-testid={`kanban-col-${st}`}
                    >
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: TASK_STATUS[st].color }} />
                        <span className="font-poppins font-semibold text-xs">{TASK_STATUS[st].label}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{boardTasks.filter((t) => t.status === st).length}</span>
                      </div>
                      <div className="space-y-2 min-h-[60px]">
                        {boardTasks.filter((t) => t.status === st).map((t) => (
                          <TaskCard
                            key={t.id}
                            task={t}
                            users={um}
                            clients={clients}
                            onClick={openEdit}
                            onStatus={setStatus}
                            onDelete={deleteTask}
                            draggable
                            dragging={dragTask?.id === t.id}
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = "move";
                              try { e.dataTransfer.setData("text/plain", t.id); } catch { /* ignore */ }
                              setDragTask(t);
                            }}
                            onDragEnd={() => { setDragTask(null); setDropColumn(null); }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="lista">
              <div className="space-y-2">
                {boardTasks.length === 0 && <p className="text-sm text-muted-foreground">Sem tarefas.</p>}
                {boardTasks.map((t) => (
                  <TaskCard key={t.id} task={t} users={um} clients={clients} onClick={openEdit} onStatus={setStatus} onDelete={deleteTask} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="calendario">
              <div className="space-y-2">
                {[...boardTasks].filter((t) => t.dueDate).sort((a, b) => a.dueDate.localeCompare(b.dueDate)).map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer hover:border-primary/40" onClick={() => openEdit(t)}>
                    <div className="text-center w-12 shrink-0">
                      <div className="font-poppins font-bold text-lg leading-none">{format(parseISO(t.dueDate), "d")}</div>
                      <div className="text-[10px] uppercase text-muted-foreground">{format(parseISO(t.dueDate), "MMM", { locale: pt })}</div>
                    </div>
                    <PriorityDot p={t.priority} />
                    <span className="font-roboto text-sm">{t.title}</span>
                    <Badge className="ml-auto rounded-full text-[10px]" style={{ backgroundColor: TASK_STATUS[t.status].color, color: "#fff" }}>{TASK_STATUS[t.status].label}</Badge>
                  </div>
                ))}
                {boardTasks.filter((t) => t.dueDate).length === 0 &&<p className="text-sm text-muted-foreground">Sem tarefas com prazo.</p>}
              </div>
            </TabsContent>

            <TabsContent value="concluidas">
              <CompletedTasksPanel team={teamView} personId={personId} users={um} clients={clients} onOpen={openEdit} />
            </TabsContent>
          </Tabs>
        </div>

      </div>

      <StickyNotes open={notepadOpen} onClose={() => setNotepadOpen(false)} />
      <TaskModal open={modalOpen} onOpenChange={setModalOpen} task={editing} />
    </div>
  );
}
